<template>
  <div>
    <div class="profile--settings--row automatic-reply--settings--row row">
      <div class="columns small-3">
        <h4 class="block-title">
          {{ $t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.TITLE') }}
        </h4>
        <p v-html="$t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.NOTE')"></p>
      </div>
      <div class="columns small-9 medium-5">
        <div
          v-for="(reply, index) in replies"
          :key="index"
          class="reply-entry"
        >
          <accordion-item
            :title="replyTitle(index)"
            :is-open="openAccordions[index]"
            @click="toggleAccordion(index)"
          >
            <div>
              <p class="status-select-label">
                {{ $t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.STATUS_SELECT_LABEL') }}
              </p>
              <div class="status-toggles">
                <label
                  v-for="status in allStatusOptions"
                  :key="status.value"
                  class="status-toggle-label"
                  :class="{ 'is-disabled': isStatusDisabled(status.value, index) }"
                >
                <div>
                  <availability-status-badge :status="status.value" />
                  {{ status.label }}
                </div>
                <woot-switch
                  size="small"
                  :value="reply.statuses.includes(status.value)"
                  @input="toggleStatus(index, status.value, $event)"
                  :disabled="isStatusDisabled(status.value, index)"
                />
                </label>
              </div>
              <woot-message-editor
                :id="`automatic-reply-input-${index}`"
                v-model="reply.message"
                class="message-editor"
                :is-format-mode="true"
                :placeholder="
                  $t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY.PLACEHOLDER')
                "
              />
              <woot-button
                variant="clear"
                color-scheme="alert"
                icon="dismiss"
                size="small"
                type="button"
                class="remove-reply-btn"
                @click="removeReply(index)"
              >
                {{ btnRemoveReplyText }}
              </woot-button>
            </div>
          </accordion-item>
        </div>

        <div class="actions-buttons">
          <woot-button
          v-if="canAddMore"
          variant="clear"
          icon="add"
          type="button"
          class="add-reply-btn"
          @click="addReply"
          >
            {{ $t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.ADD_REPLY') }}
          </woot-button>

          <woot-button
            :disabled="hasIncompleteReply || hasEmptyReplies"
            :is-loading="uiFlags.isUpdatingProfile"
            type="button"
            @click="updateAutomaticReply"
          >
            {{ $t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.BTN_TEXT') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import alertMixin from 'shared/mixins/alertMixin';
import wootConstants from 'dashboard/constants/globals';
import AvailabilityStatusBadge from 'dashboard/components/widgets/conversation/AvailabilityStatusBadge';
import AccordionItem from './components/AccordionItem.vue';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default {
  components: {
    WootMessageEditor,
    AvailabilityStatusBadge,
    AccordionItem,
  },
  mixins: [alertMixin],
  data() {
    return {
      replies: [],
      openAccordions: [],
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters({
      getAutomaticReplies: 'getAutomaticReplies',
      getCurrentAccountId: 'getCurrentAccountId',
      getAuthUIFlags: 'getAuthUIFlags',
    }),
    uiFlags() {
      return this.getAuthUIFlags;
    },
    allStatusOptions() {
      const statusLabels = this.$t(
        'PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST'
      );
      return AVAILABILITY_STATUS_KEYS.filter(key => key !== 'online').map((key, index) => ({
        value: key,
        label: statusLabels[index + 1], // +1 to skip 'online' label
      }));
    },
    usedStatuses() {
      return this.replies.flatMap(reply => reply.statuses);
    },
    canAddMore() {
      return this.usedStatuses.length < AVAILABILITY_STATUS_KEYS.length - 1 && this.replies.length < AVAILABILITY_STATUS_KEYS.length - 1;
    },
    hasIncompleteReply() {
      return this.filledReplies.some(
        r => !r.statuses.length || !r.message.trim()
      );
    },
    hasEmptyReplies() {
      return this.replies.length > 1 && this.replies.some(
        r => !r.statuses.length && !r.message.trim()
      );
    },
    filledReplies() {
      return this.replies.filter(
        r => r.statuses.length > 0 || r.message.trim()
      );
    },
    btnRemoveReplyText() {
      return this.replies.length > 1
        ? this.$t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.REMOVE_REPLY')
        : this.$t('PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.CLEAN_REPLY');
    },
  },
  mounted() {
    this.initValues();
  },
  methods: {
    initValues() {
      const saved = this.getAutomaticReplies;
      if (saved && saved.length > 0) {
        this.replies = saved.map(r => ({
          statuses: Array.isArray(r.statuses) ? [...r.statuses] : [],
          message: r.message || '',
        }));
      } else {
        this.replies = [{ statuses: [], message: '' }];
      }
      this.openAccordions = this.replies.map(() => this.replies.length === 1);
    },
    replyTitle(index) {
      const reply = this.replies[index];
      if (!reply || !reply.statuses.length) {
        return this.$t(
          'PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.REPLY_TITLE',
          { index: index + 1 }
        );
      }
      const labels = reply.statuses.map(s => {
        const opt = this.allStatusOptions.find(o => o.value === s);
        return opt ? opt.label : s;
      });
      return labels.join(', ');
    },
    toggleAccordion(index) {
      this.$set(this.openAccordions, index, !this.openAccordions[index]);
    },
    isStatusDisabled(statusValue, replyIndex) {
      return this.replies.some(
        (reply, i) => i !== replyIndex && reply.statuses.includes(statusValue)
      );
    },
    toggleStatus(replyIndex, statusValue, event) {
      const reply = this.replies[replyIndex];
      if (event) {
        this.$set(reply, 'statuses', [...reply.statuses, statusValue]);
      } else {
        this.$set(
          reply,
          'statuses',
          reply.statuses.filter(s => s !== statusValue)
        );
      }
    },
    addReply() {
      this.replies.push({ statuses: [], message: '' });
      this.openAccordions.push(true);
    },
    removeReply(index) {
      this.replies.splice(index, 1);
      this.openAccordions.splice(index, 1);
      if (this.replies.length === 0) {
        this.addReply();
      }
    },
    async updateAutomaticReply() {
      console.log('filledReplies', this.filledReplies);
      console.log('hasIncompleteReply', this.hasIncompleteReply);
      console.log('hasEmptyReplies', this.hasEmptyReplies);
      if (this.hasIncompleteReply || this.hasEmptyReplies) {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.VALIDATION_ERROR'
          )
        );
        return;
      }

      try {
        await this.$store.dispatch('updateProfile', {
          automatic_reply: this.filledReplies,
          account_id: this.getCurrentAccountId,
        });
        this.errorMessage = this.$t(
          'PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.API_SUCCESS'
        );
      } catch (error) {
        this.errorMessage = this.$t(
          'PROFILE_SETTINGS.FORM.AUTOMATIC_REPLY_SECTION.API_ERROR'
        );
        if (error?.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        }
      } finally {
        this.showAlert(this.errorMessage);
      }
    },
  },
};
</script>

<style lang="scss">
.profile--settings--row.automatic-reply--settings--row {
  align-items: start;
}

.automatic-reply--settings--row {
  .reply-entry {
    margin-bottom: var(--space-small);
  }

  .status-select-label {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-smaller);
    color: var(--s-700);
  }

  .status-toggles {
    display: grid;
    grid-template-columns: auto auto;
    margin-bottom: var(--space-normal);
  }

  .status-toggle-label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    justify-content: space-between;
    width: 55%;

    &.is-disabled {
      opacity: 0.3;
      cursor: not-allowed;

      .toggle-button {
        cursor: not-allowed;
      }
    }
  }

  .cw-accordion--title {
    background-color: #fff;
    border-radius: var(--border-radius-normal) var(--border-radius-normal) 0 0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    transition: box-shadow 0.2s ease;
    border-top: none;
  }

  .cw-accordion--content{
    background-color: #fff;
    border-top: 1px solid #e2e8f0;
    animation: deslizarBaixo 0.3s ease-out;
    border-radius: 0 0 var(--border-radius-normal) var(--border-radius-normal);
  }

  .add-reply-btn {
    margin-bottom: var(--space-small);
  }

  .ProseMirror-woot-style {
    height: auto;
  }

  .editor-root {
    background: var(--white);
    margin-bottom: var(--space-normal);
  }

  .actions-buttons{
    align-self: end;
  }
}
</style>

